import { Grid, Box, ButtonGroup, Table, TableBody } from 'components/ui';
import { FormattedMessage } from 'react-intl';
import { CheckBoxIcon, CheckBoxOutlineBlankIcon } from 'components/uiIcons';
import { StyledLink } from 'components/styles_components/link';
import { WhiteCard } from 'components/styles_components/card';
import { InspectionLink, InspectionSubjectLink, PlaceLink } from 'utils/link';
import {
  CardOptionTypography,
  CardTitleTypography,
  CardOptionHintTypography,
} from 'components/styles_components/typography';
import ShowItemTableRow from 'components/item/show_item_table_row';
import Date from 'components/date';
import EditButton from 'components/inspection_result/button/edit';
import DeleteButton from 'components/inspection_result/button/delete';
import InfoContainer from 'components/info_container';
import ResultCard from 'components/inspection/show_item/result_card';
import InspectorCard from 'components/inspection/show_item/inspector_card';
import DateCard from 'components/inspection/show_item/date_card';
import ComplianceRatioCard from 'components/inspection/show_item/compliance_ratio_card';

const valueIcon = (value) => (value ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />);

const ShowItem = ({ inspectionResult, space = {}, refetch }) => {
  const inspection = inspectionResult.inspection;

  return (
    <Grid container spacing={2}>
      <Grid container size={{ xs: 12, lg: 9 }} spacing={2}>
        <Grid size={{ xs: 12 }}>
          <WhiteCard>
            <CardTitleTypography variant="h5">
              <FormattedMessage id="inspection.result" /> <Date date={inspectionResult.doneAt} />
            </CardTitleTypography>
            <Grid container spacing={2} sx={{ mt: 2, mb: 2 }}>
              <ResultCard result={inspectionResult.result} doneAt={inspectionResult.doneAt} />
              <ComplianceRatioCard
                complianceRatio={inspectionResult.complianceRatio}
                label={<FormattedMessage id="inspection_result.compliance_ratio.description" />} />
              <InspectorCard inspector={inspectionResult.doneBy} />
              <DateCard date={inspectionResult.createdAt} title={<FormattedMessage id="inspection.result.executed_at" />} />
            </Grid>
            {inspectionResult.notes && (
              <Box sx={{ mt: 2 }}>
                <CardOptionTypography>
                  <FormattedMessage id="inspection.result.notes" />
                </CardOptionTypography>
                <CardOptionHintTypography sx={{ m: 1 }}>{inspectionResult.notes}</CardOptionHintTypography>
              </Box>
            )}
            {space.inspectorRole && (
              <Box sx={{ mt: 2 }}>
                <ButtonGroup>
                  <EditButton inspectionResult={inspectionResult} refetch={refetch} />
                  <DeleteButton inspectionResult={inspectionResult} />
                </ButtonGroup>
              </Box>
            )}
          </WhiteCard>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <WhiteCard>
            <CardOptionTypography>
              <FormattedMessage id="inspection" />
            </CardOptionTypography>
            <Table>
              <TableBody>
                <ShowItemTableRow label={<FormattedMessage id="name" />}>
                  {inspection.name}
                </ShowItemTableRow>
                <ShowItemTableRow label={<FormattedMessage id="inspection.period" />}>
                  {inspection.periodNumber}{' '}
                  <FormattedMessage id={`inspection.period.${inspection.periodCycle}`} />
                </ShowItemTableRow>
                <ShowItemTableRow label={<FormattedMessage id="inspection.due_date" />}>
                  <Date date={inspection.dueDate} emptyComponent="-" />
                </ShowItemTableRow>
              </TableBody>
            </Table>
            <Box display="flex" justifyContent="flex-end">
              <StyledLink to={InspectionLink(inspection.id)}>
                <FormattedMessage id="button.inspection.view" />
              </StyledLink>
            </Box>
          </WhiteCard>
        </Grid>
        {inspection?.inspectionSubject && (
          <Grid size={{ xs: 12 }}>
            <WhiteCard>
              <CardOptionTypography>
                <FormattedMessage id="inspection_subject" />
              </CardOptionTypography>
              <Table>
                <TableBody>
                  <ShowItemTableRow label={<FormattedMessage id="inspection_subject.brand" />}>
                    {inspection.inspectionSubject.brand || '-'}
                  </ShowItemTableRow>
                  <ShowItemTableRow label={<FormattedMessage id="inspection_subject.model" />}>
                    {inspection.inspectionSubject.model || '-'}
                  </ShowItemTableRow>
                  <ShowItemTableRow
                    label={<FormattedMessage id="inspection_subject.serial_number" />}
                  >
                    {inspection.inspectionSubject.serialNumber || '-'}
                  </ShowItemTableRow>
                </TableBody>
              </Table>
              <Box display="flex" justifyContent="flex-end">
                <StyledLink to={InspectionSubjectLink(inspection.inspectionSubject.id)}>
                  <FormattedMessage id="button.inspection_subject.view" />
                </StyledLink>
              </Box>
            </WhiteCard>
          </Grid>
        )}
        {inspection.place && (
          <Grid size={{ xs: 12 }}>
            <WhiteCard>
              <CardOptionTypography>
                <FormattedMessage id="place" />
              </CardOptionTypography>
              <Table>
                <TableBody>
                  <ShowItemTableRow label={<FormattedMessage id="place.name" />}>
                    {inspection.place.name}
                  </ShowItemTableRow>
                  <ShowItemTableRow label={<FormattedMessage id="place.street" />}>
                    {inspection.place.street}
                  </ShowItemTableRow>
                  <ShowItemTableRow label={<FormattedMessage id="place.city" />}>
                    {inspection.place.city}
                  </ShowItemTableRow>
                </TableBody>
              </Table>
              <Box display="flex" justifyContent="flex-end">
                <StyledLink to={PlaceLink(inspection.place.id)}>
                  <FormattedMessage id="button.place.view" />
                </StyledLink>
              </Box>
            </WhiteCard>
          </Grid>
        )}
      </Grid>
      <Grid size={{ xs: 12, lg: 3 }}>
        <WhiteCard>
          <CardOptionTypography>
            <FormattedMessage id="inspection.field.count" />
          </CardOptionTypography>
          {(inspectionResult.inspectionResultFields ?? []).length > 0 ? (
            <Table>
              <TableBody>
                {(inspectionResult.inspectionResultFields ?? []).map((field) => (
                  <ShowItemTableRow key={field.id} label={field.name} emptyComponent="">
                    {valueIcon(field.value)}
                  </ShowItemTableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <InfoContainer>
                <FormattedMessage id="inspection.fields.empty" />
              </InfoContainer>
            </Box>
          )}
        </WhiteCard>
      </Grid>
    </Grid>
  );
};

export default ShowItem;
